import React, {useRef, useEffect, useState} from 'react';
import {Box, Typography, Stack} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ColorizeIcon from '@mui/icons-material/Colorize'; // Import syringe/injection icon

const HealthCategoryNav = ({
  categories,
  activeIndex,
  onSelectCategory,
  isLargeScreen,
  alignment = 'flex-start', // Default
  indicatorpadding = 2, // Default
  clientBaseNavCss = {
    arrowColor: '#ff5722',
    arrowHOverColor: 'ff5722',
    movingIndicatorColor: '#ff5722',
  },
}) => {
  // Add safety check for categories
  const healthCategories = Array.isArray(categories) ? categories : [];

  // Early return if no categories
  if (healthCategories.length === 0) {
    return null; // or return a loading state
  }

  // Reference to the Stack component for scrolling
  const scrollContainerRef = useRef(null);

  // Refs for each category item
  const categoryRefs = useRef([]);

  // State to track indicator position and width
  const [indicatorStyle, setIndicatorStyle] = useState({
    left: 0,
    width: 0,
  });

  // Set up category refs
  useEffect(() => {
    categoryRefs.current = categoryRefs.current.slice(0, healthCategories.length);
  }, [healthCategories]);

  // Update indicator position when active index changes or on window resize
  useEffect(() => {
    const updateIndicatorPosition = () => {
      if (categoryRefs.current[activeIndex] && scrollContainerRef.current) {
        const activeElement = categoryRefs.current[activeIndex];
        const containerOffset = scrollContainerRef.current.scrollLeft;

        // Calculate position relative to the scrollable container
        // Adjust for container's padding by adding px value
        const leftPadding = indicatorpadding; // Equivalent to px: { xs: 1, sm: 2 } in default theme

        setIndicatorStyle({
          left: activeElement.offsetLeft - containerOffset + leftPadding,
          width: activeElement.offsetWidth,
        });
      }
    };

    // Initial update
    updateIndicatorPosition();

    // Update on scroll as well
    if (scrollContainerRef.current) {
      scrollContainerRef.current.addEventListener('scroll', updateIndicatorPosition);
    }

    // Add window resize listener to handle responsive adjustments
    window.addEventListener('resize', updateIndicatorPosition);

    return () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener('scroll', updateIndicatorPosition);
      }
      window.removeEventListener('resize', updateIndicatorPosition);
    };
  }, [activeIndex, healthCategories.length]); // Add healthCategories.length as dependency

  // Scroll to make active item visible
  useEffect(() => {
    if (scrollContainerRef.current && categoryRefs.current[activeIndex]) {
      const container = scrollContainerRef.current;
      const activeItem = categoryRefs.current[activeIndex];

      // Get positions and dimensions
      const containerRect = container.getBoundingClientRect();
      const activeItemRect = activeItem.getBoundingClientRect();

      // Check if item is not fully visible
      if (activeItemRect.right > containerRect.right || activeItemRect.left < containerRect.left) {
        // Calculate position to center the item
        const scrollPosition = activeItem.offsetLeft - container.offsetWidth / 2 + activeItem.offsetWidth / 2;

        // Smooth scroll to the item
        container.scrollTo({
          left: scrollPosition,
          behavior: 'smooth',
        });
      }
    }
  }, [activeIndex]);

  const handlePrevious = () => {
    if (activeIndex > 0) {
      onSelectCategory(activeIndex - 1);
    }
  };

  const handleNext = () => {
    if (activeIndex < healthCategories.length - 1) {
      onSelectCategory(activeIndex + 1);
    }
  };

  const handleCategoryClick = index => {
    onSelectCategory(index);
  };

  const breakLongText = (text, maxLength = 10) => {
    // Function to break text into manageable chunks with hyphenation
    const words = text.split(/\s+/);
    const brokenWords = words.flatMap(word => {
      // Break words longer than maxLength
      if (word.length > maxLength) {
        const chunks = [];
        for (let i = 0; i < word.length; i += maxLength) {
          // Add hyphen to all chunks except the last one
          const chunk = word.slice(i, i + maxLength);
          const isLastChunk = i + maxLength >= word.length;
          chunks.push(isLastChunk ? chunk : `${chunk}-`);
        }
        return chunks;
      }
      return [word];
    });

    // Combine words, adding line breaks as needed
    const lines = [];
    let currentLine = [];
    let currentLineLength = 0;

    brokenWords.forEach(word => {
      if (currentLineLength + word.length > maxLength) {
        lines.push(currentLine.join(' '));
        currentLine = [word];
        currentLineLength = word.length;
      } else {
        currentLine.push(word);
        currentLineLength += word.length + 1; // +1 for space
      }
    });

    if (currentLine.length > 0) {
      lines.push(currentLine.join(' '));
    }

    return lines;
  };

  return (
    <Box sx={{width: '100%', display: 'flex', justifyContent: 'center'}}>
      <Box
        sx={{
          width: isLargeScreen ? '35%' : '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: isLargeScreen ? 0 : 2,
          mb: 2,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {/* Left Arrow */}
          <Box
            onClick={handlePrevious}
            sx={{
              cursor: activeIndex > 0 ? 'pointer' : 'default',
              // backgroundColor: activeIndex > 0 ? '#ff5722' : '#aaa',
              backgroundColor: clientBaseNavCss.arrowColor,
              borderRadius: '50%',
              width: {xs: 24, sm: 32},
              height: {xs: 24, sm: 32},
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 2,
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.25)',
              '&:hover': {
                // backgroundColor: activeIndex > 0 ? '#f44336' : '#aaa'
                backgroundColor: clientBaseNavCss.arrowColor,
              },
              position: 'relative',
              top: 22,
            }}
          >
            <ArrowBackIosNewIcon sx={{color: '#fff', fontSize: {xs: 12, sm: 16}}} />
          </Box>

          {/* Category Navigation Container */}
          <Box
            sx={{
              position: 'relative',
              width: 'calc(100% - 80px)',
              maxWidth: {xs: 'calc(100% - 80px)', sm: 'auto'},
              overflow: 'hidden',
              display: 'flex',
              justifyContent: alignment,
              alignItems: 'flex-start',
            }}
          >
            {/* Category Titles */}
            <Stack
              ref={scrollContainerRef}
              direction="row"
              sx={{
                maxWidth: '320px',
                px: {xs: 1, sm: 2},
                zIndex: 1,
                overflow: 'auto',
                position: 'relative',
                pb: 3, // Add padding at bottom for the indicator bar
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            >
              {healthCategories.map((category, index) => (
                <Box
                  key={category.id}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    ref={el => (categoryRefs.current[index] = el)}
                    onClick={() => handleCategoryClick(index)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      flex: 0.9,
                      minWidth: {xs: 80, sm: 90, lg: 120},
                      maxWidth: {xs: 100, sm: 100},
                      height: 80,
                      mx: 0,
                      position: 'relative',
                      pt: 0.5,
                    }}
                  >
                    {category.id === 'products' ? (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        <AutoAwesomeIcon
                          sx={{
                            color: activeIndex === index ? clientBaseNavCss.startIconColorActive : '#aaa',
                            fontSize: {xs: 20, sm: 24},
                            fontWeight: activeIndex === index ? 700 : 400,
                          }}
                        />
                      </Box>
                    ) : category.id === 'labtest' ? (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        <ColorizeIcon
                          sx={{
                            color: activeIndex === index ? clientBaseNavCss.startIconColorActive || '#ff5722' : '#aaa',
                            fontSize: {xs: 20, sm: 24},
                            fontWeight: activeIndex === index ? 700 : 400,
                          }}
                        />
                      </Box>
                    ) : // Render category text with comprehensive word breaking
                    category.display && Array.isArray(category.display) ? (
                      category.display.map((line, lineIndex) =>
                        breakLongText(line).map((brokenLine, brokenIndex) => (
                          <Typography
                            key={`${lineIndex}-${brokenIndex}`}
                            variant="body2"
                            component="div"
                            align="center"
                            sx={{
                              fontSize: {xs: '10px', sm: '12px'},
                              lineHeight: 1.2,
                              fontWeight: activeIndex === index ? 700 : 400,
                              color: activeIndex === index ? '#000' : '#777',
                              transition: 'all 0.3s ease',
                              whiteSpace: 'normal',
                              marginBottom: 0.25,
                              width: '100%',
                              textAlign: 'center',
                              wordBreak: 'break-word',
                            }}
                          >
                            {brokenLine}
                          </Typography>
                        )),
                      )
                    ) : (
                      breakLongText(category.name || 'Unknown').map((brokenLine, brokenIndex) => (
                        <Typography
                          key={brokenIndex}
                          variant="body2"
                          component="div"
                          align="center"
                          sx={{
                            fontSize: {xs: '10px', sm: '12px'},
                            lineHeight: 1.2,
                            fontWeight: activeIndex === index ? 700 : 400,
                            color: activeIndex === index ? '#000' : '#777',
                            transition: 'all 0.3s ease',
                            whiteSpace: 'normal',
                            marginBottom: 0.25,
                            width: '100%',
                            textAlign: 'center',
                            wordBreak: 'break-word',
                          }}
                        >
                          {brokenLine}
                        </Typography>
                      ))
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>

            {/* Category Navigation Indicator */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                px: {xs: 1, sm: 2},
              }}
            >
              {/* Full-width background bar */}
              <Box
                sx={{
                  height: 18,
                  backgroundColor: '#ddd',
                  borderRadius: 4,
                  zIndex: 0,
                  width: '100%',
                }}
              />

              {/* Moving indicator */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: indicatorStyle.left,
                  width: indicatorStyle.width,
                  height: 18,
                  // backgroundColor: '#ff5722',
                  backgroundColor: clientBaseNavCss.movingIndicatorColor,

                  borderRadius: 4,
                  transition: 'all 0.3s ease',
                  zIndex: 1,
                }}
              />
            </Box>
          </Box>

          {/* Right Arrow */}
          <Box
            onClick={handleNext}
            sx={{
              cursor: activeIndex < healthCategories.length - 1 ? 'pointer' : 'default',
              // backgroundColor: activeIndex < healthCategories.length - 1 ? '#ff5722' : '#aaa',
              backgroundColor: clientBaseNavCss.arrowColor,
              borderRadius: '50%',
              width: {xs: 24, sm: 32},
              height: {xs: 24, sm: 32},
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 2,
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.25)',
              '&:hover': {
                // backgroundColor: activeIndex < healthCategories.length - 1 ? '#f44336' : '#aaa'
                backgroundColor: clientBaseNavCss.arrowColor,
              },
              position: 'relative',
              top: 22,
            }}
          >
            <ArrowForwardIosIcon sx={{color: '#fff', fontSize: {xs: 12, sm: 16}}} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default HealthCategoryNav;
